import { QUEUE_TABLE, QUEUE_SCROLL, QUEUE_THEAD, QUEUE_TH, QUEUE_TD } from './queueTableStyles';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { fmtDate, daysPastDeadline } from '../../lib/dates';
import { OttoSleeping } from '../otto';

// "Waiting on you" tab (waiting-on-you brief) — the things a supplier is
// waiting for, in ONE tab, GROUPED, each group keeping its own sort because
// their urgency runs in opposite directions:
//   • Questions — urgency is time REMAINING (a brief closing soon) → close date
//     ascending.
//   • Extension requests — also time REMAINING, and harder: once the brief
//     closes the request is moot, so an unanswered one EXPIRES rather than just
//     ageing → close date ascending.
//   • Update requests — urgency is time ELAPSED (a badly overdue brief) → days
//     overdue descending.
// Ordered by where the supplier stands relative to the deadline: before it
// (blocked), running out of it, past it.
// Merged into one flat list, one is always ordered wrongly — so each group
// states its sort. Data comes in as props (loaded once by the parent) so the
// group counts match the tab + banner counts.

const waitingLabel = (createdAt) => {
  const ms = Date.now() - new Date(createdAt).getTime();
  if (isNaN(ms)) return '—';
  const days = Math.floor(ms / 86400000);
  if (days >= 1) return `${days} day${days === 1 ? '' : 's'}`;
  const hours = Math.floor(ms / 3600000);
  if (hours >= 1) return `${hours} hour${hours === 1 ? '' : 's'}`;
  const mins = Math.max(1, Math.floor(ms / 60000));
  return `${mins} min${mins === 1 ? '' : 's'}`;
};

// Waiting-time colour (tidy-up §6, matching admin-questions-tidyup §4):
// under 24h reads neutral so a three-hour-old question isn't dressed up
// like a two-day-old one; 1–3 days amber; 4 days or more red.
const waitingTone = (createdAt) => {
  const ms = Date.now() - new Date(createdAt).getTime();
  if (isNaN(ms)) return 'text-[#51534A]';
  const days = ms / 86400000;
  if (days < 1) return 'text-[#51534A]';   // under 24 hours — neutral grey
  if (days < 4) return 'text-[#7A4E10]';    // 1–3 days — amber
  return 'text-[#E0290A]';                   // 4 days or more — red
};

// Brief cell: reference leads (what people search by), title beneath, smaller.
function BriefCell({ brief }) {
  return (
    <div className="min-w-0">
      <p className="font-['Metropolis'] text-sm font-semibold text-ink truncate">{brief?.fusion_project_number || '—'}</p>
      {brief?.brief_name && <p className="font-['Metropolis'] text-[11px] text-[#51534A] truncate">{brief.brief_name}</p>}
    </div>
  );
}

// Match the List view table styling (bg-table-header header, mid-grey
// 0.18em labels, zebra rows with table-hover) so the tabs read as one table.
// Shared with the other queue tabs so the four line up (queueTableStyles).
// This was py-3 where the others were py-2.5, which is why the header band
// changed height as you moved between tabs.
const TH = QUEUE_TH;
const TD = QUEUE_TD;
const ROW = (idx) => `${idx % 2 === 1 ? 'bg-table-zebra' : 'bg-white'} hover:bg-table-hover transition-colors border-t border-table-divider`;

// A brief closing within 48h makes an unanswered question urgent — the
// "Brief closes" value is emphasised for those (separating-questions §4).
const closesSoon = (iso) => {
  if (!iso) return false;
  const ms = new Date(iso).getTime() - Date.now();
  return ms > 0 && ms <= 48 * 3600 * 1000;
};

const SpeechIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>
);
const HourglassIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 2h12"/><path d="M6 22h12"/><path d="M6 2v5a6 6 0 0 0 6 6 6 6 0 0 0 6-6V2"/><path d="M6 22v-5a6 6 0 0 1 6-6 6 6 0 0 1 6 6v5"/></svg>
);
const ClockIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15 14"/></svg>
);

// Section head — describes the supplier's POSITION, not the record type
// (separating-questions §2/§3). Red = a supplier blocked from quoting;
// blue = one who's quoted and waits on a decision.
function SectionHead({ tone, icon, title, sub, count }) {
  const tint = tone === 'red' ? 'bg-[#FCEAE6] text-[#D8341A]'
    : tone === 'amber' ? 'bg-[#FBEEDA] text-[#8A5512]'
    : 'bg-[#E4F0F4] text-[#1F6C87]';
  return (
    <div className="flex items-start gap-3 mb-3">
      <span className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${tint}`} aria-hidden="true">{icon}</span>
      <div className="min-w-0">
        <h3 className="font-['Metropolis'] text-[14px] font-bold text-ink flex items-center gap-2 flex-wrap">
          {title}
          <span className={`font-['Metropolis'] text-[11px] font-bold rounded-full px-2 py-0.5 ${tint}`}>{count}</span>
        </h3>
        <p className="font-['Metropolis'] text-[11.5px] text-[#51534A] mt-0.5 max-w-[76ch] leading-relaxed">{sub}</p>
      </div>
    </div>
  );
}

export default function WaitingOnYou({ questions = [], extensionRequests = [], updateRequests = [] }) {
  const navigate = useNavigate();

  const sortedQuestions = useMemo(() => questions.slice().sort((a, b) => {
    const ca = a.briefs?.required_response_time ? new Date(a.briefs.required_response_time).getTime() : Infinity;
    const cb = b.briefs?.required_response_time ? new Date(b.briefs.required_response_time).getTime() : Infinity;
    if (ca !== cb) return ca - cb;                 // brief close soonest first
    return new Date(a.created_at) - new Date(b.created_at);
  }), [questions]);

  const sortedUpdates = useMemo(() => updateRequests.slice().sort((a, b) => {
    const oa = daysPastDeadline(a.briefs?.required_response_time) || 0;
    const ob = daysPastDeadline(b.briefs?.required_response_time) || 0;
    return ob - oa;                                // most overdue first
  }), [updateRequests]);

  // Same sort as questions — soonest-closing first. An extension request is
  // answerable only while the brief is open, so the closing date is the
  // deadline on the DECISION, not just a measure of how urgent it feels.
  const sortedExtensions = useMemo(() => extensionRequests.slice().sort((a, b) => {
    const ca = a.briefs?.required_response_time ? new Date(a.briefs.required_response_time).getTime() : Infinity;
    const cb = b.briefs?.required_response_time ? new Date(b.briefs.required_response_time).getTime() : Infinity;
    if (ca !== cb) return ca - cb;
    return new Date(a.extension_requested_at) - new Date(b.extension_requested_at);
  }), [extensionRequests]);

  const total = questions.length + extensionRequests.length + updateRequests.length;

  // Fully-empty (§3) — one centred sleeping-Otto state, not two headings
  // and two empty lines. Voice follows the dashboard's sleeping-Otto
  // pattern: short declarative, one-word affirmation, then what would
  // appear here. "You're all caught up" (not "keep it up") — an empty
  // queue is a thing you've finished, not an ongoing good state.
  if (total === 0) {
    return (
      <div className="px-8 py-6">
        <div className="bg-white border border-line rounded-xl px-8 py-14 text-center">
          <div className="flex justify-center mb-4"><OttoSleeping size={128} /></div>
          <p className="font-['Metropolis'] text-base font-semibold text-ink">Nobody's waiting on you. Nice.</p>
          <p className="font-['Metropolis'] text-sm text-fusion-mid-grey mt-1 max-w-[46ch] mx-auto">
            No open questions, no requests for more time, no update requests. You're all caught up — anything a supplier is waiting on you for, across your live briefs, would show here.
          </p>
        </div>
      </div>
    );
  }

  // Empty group (the other has items, §5) — keep the descriptive heading so
  // the distinction stays visible, and collapse the body to one muted line.
  const nothingLine = <p className="font-['Metropolis'] text-sm text-[#51534A] pl-14">Nothing waiting.</p>;

  return (
    <div className="px-8 py-6 space-y-9">
      {/* Questions — before the deadline: they can't quote until you answer. */}
      <section aria-labelledby="woy-questions-heading">
        <SectionHead
          tone="red"
          icon={SpeechIcon}
          count={questions.length}
          title={<span id="woy-questions-heading">They can't quote until you answer</span>}
          sub="Questions asked before the deadline. Until you reply the supplier can't price the job properly — so the closer the brief is to closing, the more it costs." />
        {questions.length === 0 ? nothingLine : (
          <div className={`bg-white border border-line border-l-[3px] border-l-[#D8341A] rounded-xl ${QUEUE_SCROLL}`}>
            <table className={`${QUEUE_TABLE} min-w-[900px]`}>
              <thead className={QUEUE_THEAD}>
                <tr>
                  <th scope="col" className={TH}>Question</th>
                  <th scope="col" className={TH}>Supplier</th>
                  <th scope="col" className={TH}>Brief</th>
                  <th scope="col" className={TH}>Waiting</th>
                  <th scope="col" className={TH}>Brief closes</th>
                  <th scope="col" className={TH}><span className="sr-only">Action</span></th>
                </tr>
              </thead>
              <tbody>
                {sortedQuestions.map((q, idx) => {
                  const soon = closesSoon(q.briefs?.required_response_time);
                  return (
                    <tr key={q.id} className={ROW(idx)}>
                      <td className={`${TD} max-w-[320px]`}><p className="font-['Metropolis'] text-sm text-ink">{q.body}</p></td>
                      <td className={`${TD} font-['Metropolis'] text-sm text-ink`}>{q.suppliers?.supplier_name || '—'}</td>
                      <td className={TD}><BriefCell brief={q.briefs} /></td>
                      <td className={`${TD} font-['Metropolis'] text-sm font-semibold ${waitingTone(q.created_at)}`}>{waitingLabel(q.created_at)}</td>
                      <td className={`${TD} font-['Metropolis'] text-sm ${soon ? 'font-bold text-[#D8341A]' : 'text-ink'}`}>{q.briefs?.required_response_time ? fmtDate(q.briefs.required_response_time) : '—'}</td>
                      <td className={TD}>
                        <button type="button" onClick={() => navigate(`/briefs/${q.brief_id}#brief-question-${q.id}`)}
                          className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.12em] text-fusion-forest border border-[#92D400]/50 hover:bg-[#92D400]/10 rounded-full px-3 py-1.5 transition-colors whitespace-nowrap">
                          Answer
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Extension requests — running out of time BEFORE the deadline. Sits
          between the other two because that is where it sits in time: they are
          not blocked waiting on an answer, and they have not quoted yet. */}
      <section aria-labelledby="woy-extensions-heading">
        <SectionHead
          tone="amber"
          icon={HourglassIcon}
          count={extensionRequests.length}
          title={<span id="woy-extensions-heading">They can't finish in time and have asked for more</span>}
          sub="Requests for a longer deadline. Answering late is close to answering no — once the brief closes the request is moot, and a supplier who never hears back has to choose between rushing the quote and dropping out." />
        {extensionRequests.length === 0 ? nothingLine : (
          <div className={`bg-white border border-line border-l-[3px] border-l-[#C97A16] rounded-xl ${QUEUE_SCROLL}`}>
            <table className={`${QUEUE_TABLE} min-w-[900px]`}>
              <thead className={QUEUE_THEAD}>
                <tr>
                  <th scope="col" className={TH}>Reason given</th>
                  <th scope="col" className={TH}>Supplier</th>
                  <th scope="col" className={TH}>Brief</th>
                  <th scope="col" className={TH}>Waiting</th>
                  <th scope="col" className={TH}>Brief closes</th>
                  <th scope="col" className={TH}><span className="sr-only">Action</span></th>
                </tr>
              </thead>
              <tbody>
                {sortedExtensions.map((e, idx) => {
                  const soon = closesSoon(e.briefs?.required_response_time);
                  return (
                    <tr key={e.id} className={ROW(idx)}>
                      <td className={`${TD} max-w-[320px]`}>
                        {/* The note is optional by design — the supplier signals
                            they want more time and may not say why. */}
                        {e.extension_request_note && e.extension_request_note.trim()
                          ? <p className="font-['Metropolis'] text-sm text-[#5E3D05]">{e.extension_request_note.trim()}</p>
                          : <p className="font-['Metropolis'] text-sm text-[#51534A] italic">No reason given</p>}
                      </td>
                      <td className={`${TD} font-['Metropolis'] text-sm text-ink`}>{e.suppliers?.supplier_name || '—'}</td>
                      <td className={TD}><BriefCell brief={e.briefs} /></td>
                      <td className={`${TD} font-['Metropolis'] text-sm font-semibold ${waitingTone(e.extension_requested_at)}`}>{waitingLabel(e.extension_requested_at)}</td>
                      <td className={`${TD} font-['Metropolis'] text-sm ${soon ? 'font-bold text-[#D8341A]' : 'text-ink'}`}>{e.briefs?.required_response_time ? fmtDate(e.briefs.required_response_time) : '—'}</td>
                      <td className={TD}>
                        {/* Granting the extension is the Extend Deadline tool on
                            the brief, which is what clears the flag — so land on
                            the recipients table where the request is shown. */}
                        <button type="button" onClick={() => navigate(`/briefs/${e.brief_id}#brief-recipients-section`)}
                          className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.12em] text-fusion-forest border border-[#92D400]/50 hover:bg-[#92D400]/10 rounded-full px-3 py-1.5 transition-colors whitespace-nowrap">
                          Review
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Update requests — after the deadline: they've quoted and wait on a decision. */}
      <section aria-labelledby="woy-updates-heading">
        <SectionHead
          tone="blue"
          icon={ClockIcon}
          count={updateRequests.length}
          title={<span id="woy-updates-heading">They've quoted and are waiting on your decision</span>}
          sub="Chases that arrived after the deadline. They've already done the work of quoting and may be holding crew or kit — answer with a decision, or tell them when to expect one." />
        {updateRequests.length === 0 ? nothingLine : (
          <div className={`bg-white border border-line border-l-[3px] border-l-[#1F6C87] rounded-xl ${QUEUE_SCROLL}`}>
            <table className={`${QUEUE_TABLE} min-w-[900px]`}>
              <thead className={QUEUE_THEAD}>
                <tr>
                  <th scope="col" className={TH}>Message</th>
                  <th scope="col" className={TH}>Supplier</th>
                  <th scope="col" className={TH}>Brief</th>
                  <th scope="col" className={TH}>Waiting</th>
                  <th scope="col" className={TH}>Brief overdue</th>
                  <th scope="col" className={TH}><span className="sr-only">Action</span></th>
                </tr>
              </thead>
              <tbody>
                {sortedUpdates.map((u, idx) => {
                  const overdue = daysPastDeadline(u.briefs?.required_response_time) || 0;
                  return (
                    <tr key={u.id} className={ROW(idx)}>
                      <td className={`${TD} max-w-[320px]`}>
                        {u.message && u.message.trim()
                          ? <p className="font-['Metropolis'] text-sm text-[#5E3D05]">{u.message.trim()}</p>
                          : <p className="font-['Metropolis'] text-sm text-[#51534A] italic">Asked for an update</p>}
                      </td>
                      <td className={`${TD} font-['Metropolis'] text-sm text-ink`}>{u.suppliers?.supplier_name || '—'}</td>
                      <td className={TD}><BriefCell brief={u.briefs} /></td>
                      <td className={`${TD} font-['Metropolis'] text-sm font-semibold ${waitingTone(u.created_at)}`}>{waitingLabel(u.created_at)}</td>
                      <td className={TD}>
                        <span className={`font-['Metropolis'] text-[11px] font-semibold rounded-md px-2 py-0.5 border ${overdue >= 4 ? 'text-[#E0290A] bg-[#E0290A]/10 border-[#E0290A]/40' : 'text-[#7A4E10] bg-[#E89A2E]/15 border-[#E89A2E]/40'}`}>
                          {overdue} day{overdue === 1 ? '' : 's'}
                        </span>
                      </td>
                      <td className={TD}>
                        <button type="button" onClick={() => navigate(`/briefs/${u.brief_id}#brief-update-requests-section`)}
                          className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.12em] text-fusion-forest border border-[#92D400]/50 hover:bg-[#92D400]/10 rounded-full px-3 py-1.5 transition-colors whitespace-nowrap">
                          Respond
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
